import { InternalAccount } from "gen/coa/models_pb"
import { useCallback } from "react"
import { Money } from "shared-ts/classes/money"
import { UUID } from "shared-ts/classes/uuid"

import { Allocation } from "features/admin/banking/MultiAllocation"
import { useListMappingsQuery } from "features/api/accounting"

interface UsePayableAllocationRes {
  isLoading: boolean
  hasPayableAccount: (allocations: Allocation[]) => boolean
  payableAllocation: (allocations: Allocation[]) => Money
}

export function usePayableAllocation(): UsePayableAllocationRes {
  const { currentData, isLoading } = useListMappingsQuery({})

  const hasPayableAccount = useCallback(
    (allocations: Allocation[]) => {
      if (!currentData) return false
      const payableMappings = currentData.mappings.filter(
        (mapping) =>
          mapping.internalAccount === InternalAccount.LIABILITY_ACCOUNTS_PAYABLE
      )
      return payableMappings.some((mapping) =>
        allocations.some((allocation) =>
          UUID.eqFromPB(allocation.account?.id, mapping.accountId)
        )
      )
    },
    [currentData]
  )

  const payableAllocation = useCallback(
    (allocations: Allocation[]) => {
      if (!currentData) return Money.zero()
      const payableMappings = currentData.mappings.filter(
        (mapping) =>
          mapping.internalAccount === InternalAccount.LIABILITY_ACCOUNTS_PAYABLE
      )
      return Money.sum(
        ...allocations
          .filter((allocation) =>
            payableMappings.some((mapping) =>
              UUID.eqFromPB(allocation.account?.id, mapping.accountId)
            )
          )
          .map((allocation) => allocation.absoluteAmount)
      )
    },
    [currentData]
  )

  return { isLoading, hasPayableAccount, payableAllocation }
}
